import { useEffect, useState } from 'react';
import {
  DefaultButton,
  DetailsList,
  Panel,
  PanelType,
  SelectionMode,
  Stack,
  Text,
} from '@fluentui/react';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  getArchivedMetadatas,
  updateMetadataStatus,
} from '../../services/metadataService';
import UpdateMetadataStatusPanel from './UpdateMetadataStatusPanel';
import {
  METADATA_STATUS_TRANSITIONS,
  type MetadataStatus,
} from './metadataStatus';

interface IArchivedMetadataItem {
  key: number;
  metadataId: number;
  displayName: string;
  entityType: string;
  name?: string;
}

interface IStatusTarget {
  metadataId: number;
  status: string;
  displayName?: string;
}

interface ArchivedMetadatasPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  onChanged?: () => void;
}

const getAllowedOptions = (status: string) =>
  (METADATA_STATUS_TRANSITIONS[status as MetadataStatus] ?? ['Draft']).map(
    (value) => ({ key: value, text: value })
  );

const getAllowedText = (status: string) =>
  `Allowed: ${status} -> ${getAllowedOptions(status)
    .map((option) => option.text)
    .join(' / ')}`;

const ArchivedMetadatasPanel = ({
  isOpen,
  onDismiss,
  onChanged,
}: ArchivedMetadatasPanelProps) => {
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [items, setItems] = useState<IArchivedMetadataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusTarget, setStatusTarget] = useState<IStatusTarget>();
  const [statusSelection, setStatusSelection] = useState('Draft');
  const [statusComment, setStatusComment] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const abortController = new AbortController();
    setLoading(true);
    setIsInProgress(true);
    getArchivedMetadatas(abortController)
      .then((response) => {
        const next = Object.entries(response?.metadatas ?? {}).flatMap(
          ([id, value]) => {
            const metadataId = Number(id);
            if (Number.isNaN(metadataId)) return [];
            const [entity, ...nameParts] = value.split('|');
            const name = nameParts.join('|');
            return [
              {
                key: metadataId,
                metadataId,
                displayName: name ? `${entity}|${name}` : entity,
                entityType: entity,
                name: name || undefined,
              },
            ];
          }
        );
        next.sort((left, right) =>
          left.displayName.localeCompare(right.displayName)
        );
        setItems(next);
      })
      .catch((error: any) => setErrorMessage(error.message))
      .finally(() => {
        setLoading(false);
        setIsInProgress(false);
      });
    return () => abortController.abort();
  }, [isOpen, setErrorMessage, setIsInProgress]);

  const openStatusDialog = (target: IStatusTarget) => {
    setStatusTarget(target);
    setStatusSelection(target.status);
    setStatusComment('');
  };

  const dismissStatusDialog = () => {
    setStatusTarget(undefined);
  };

  const handleStatusUpdate = async () => {
    if (!statusTarget?.metadataId) {
      setErrorMessage('Metadata id is missing for status update.');
      return;
    }
    const abortController = new AbortController();
    try {
      setIsInProgress(true);
      const response = await updateMetadataStatus(
        abortController,
        statusTarget.metadataId,
        {
          status: statusSelection,
          action: statusSelection === 'Draft' ? 'Unarchived' : statusSelection,
          comment: statusComment.trim() || undefined,
        }
      );
      if (response.status !== 'Archived') {
        setItems((prev) =>
          prev.filter((item) => item.metadataId !== statusTarget.metadataId)
        );
      }
      setSuccessMessage(`Status updated to ${response.status}.`);
      onChanged?.();
      dismissStatusDialog();
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
  };

  const statusPanelStatus = statusTarget?.status ?? 'Archived';

  return (
    <>
      <Panel
        isOpen={isOpen}
        onDismiss={onDismiss}
        type={PanelType.smallFixedFar}
        closeButtonAriaLabel="Close"
        headerText="Archived metadatas"
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Text variant="small" style={{ color: '#605e5c' }}>
            Select an archived metadata to update its status.
          </Text>
          {loading ? (
            <Text>Loading archived metadatas...</Text>
          ) : items.length === 0 ? (
            <Text>No archived metadatas found.</Text>
          ) : (
            <DetailsList
              items={items}
              columns={[
                {
                  key: 'name',
                  name: 'Metadata',
                  fieldName: 'displayName',
                  minWidth: 180,
                },
                {
                  key: 'action',
                  name: 'Action',
                  minWidth: 90,
                  onRender: (item: IArchivedMetadataItem) => (
                    <DefaultButton
                      text="Change status"
                      onClick={() =>
                        openStatusDialog({
                          metadataId: item.metadataId,
                          status: 'Archived',
                          displayName: item.displayName,
                        })
                      }
                    />
                  ),
                },
              ]}
              selectionMode={SelectionMode.none}
              onItemInvoked={(item: IArchivedMetadataItem) =>
                openStatusDialog({
                  metadataId: item.metadataId,
                  status: 'Archived',
                  displayName: item.displayName,
                })
              }
            />
          )}
        </Stack>
      </Panel>
      <UpdateMetadataStatusPanel
        isOpen={!!statusTarget}
        onDismiss={dismissStatusDialog}
        statusSelection={statusSelection}
        onStatusChange={setStatusSelection}
        statusComment={statusComment}
        onCommentChange={setStatusComment}
        allowedOptions={getAllowedOptions(statusPanelStatus)}
        allowedText={getAllowedText(statusPanelStatus)}
        targetDisplayName={statusTarget?.displayName}
        updateDisabled={!statusTarget?.metadataId}
        onCancel={dismissStatusDialog}
        onUpdate={handleStatusUpdate}
      />
    </>
  );
};

export default ArchivedMetadatasPanel;
